import React, { useState, useEffect } from "react";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Container,
    Chip,
    Divider,
    Card,
    CardContent,
    useTheme
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

const theme = createTheme({
    palette: {
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
        background: {
            default: '#f5f5f5',
        },
    },
    typography: {
        h4: {
            fontWeight: 600,
        },
        h6: {
            fontWeight: 500,
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                },
            },
        },
    },
});


const RecognitionScreen = () => {
    const [screen, setScreen] = useState("read");
    const [item, setItem] = useState(null);
    const [recognitions, setRecognitions] = useState([]);

    useEffect(() => {
        const fetchRecognitionData = async () => {
            try {
                const response = await fetch(
                    "http://biometick.com/get-all-attendance/" + localStorage.getItem("userID"),
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("token")}`
                        }
                    }
                );
                const data = await response.json();
                console.log(data);
                if (data.length > 0) {
                    setRecognitions(data);
                }
            } catch (error) {
                console.error("Error fetching recognition data:", error);
            }
        };

        fetchRecognitionData();
    }, []);

    const handleEdit = (item) => {
        setItem(item);
        setScreen("modify");
    };

    // Format date and time
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const formatTime = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    };

    // Helper to split date time
    const splitDateTime = (dateTimeString) => {
        if (!dateTimeString) return { date: "N/A", time: "N/A" };
        const parts = dateTimeString.toString().split("T");
        if (parts.length < 2) return { date: "N/A", time: "N/A" };

        return {
            date: parts[0],
            time: parts[1].split(".")[0]
        };
    };

    return (<ThemeProvider theme={theme}>
        <Container maxWidth="lg" sx={{ py: 2 }}>
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" color="primary">
                        Attendance
                    </Typography>
                </Box>
                <Divider sx={{ mt: 2 }} />
            </Box>

            {screen === "read" && (<TableContainer component={Paper} elevation={2} sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Table aria-label="department table">
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Company</TableCell>
                            <TableCell>Department</TableCell>
                            <TableCell>Designation</TableCell>
                            <TableCell>Entry Time</TableCell>
                            <TableCell>Exit Time</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {recognitions.map((recognition) => {
                            if (recognition.deviceID === "registered") return null;

                            const entryDateTime = splitDateTime(recognition.entryTime);
                            const exitDateTime = splitDateTime(recognition.exitTime);

                            return (
                                <TableRow
                                    key={recognition._id}
                                    sx={{
                                        "&:hover": {
                                            backgroundColor: theme.palette.action.hover
                                        }
                                    }}
                                >
                                    <TableCell>
                                        <Avatar
                                            src={`http://biometick.com/${recognition.employee.photoUrl}`}
                                            alt={recognition.employee.name}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body1" fontWeight="500">
                                            {recognition.employee.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>{recognition.employee.company.name}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={recognition.employee.department.name}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{recognition.employee.designation.name}</TableCell>
                                    <TableCell>
                                        <Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                                <CalendarMonthIcon fontSize="small" color="primary" />
                                                <Typography variant="body2">{entryDateTime.date}</Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                <AccessTimeIcon fontSize="small" color="primary" />
                                                <Typography variant="body2">{entryDateTime.time}</Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {recognition.type === "exit" ? (
                                            <Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                                    <CalendarMonthIcon fontSize="small" color="primary" />
                                                    <Typography variant="body2">{exitDateTime.date}</Typography>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <AccessTimeIcon fontSize="small" color="primary" />
                                                    <Typography variant="body2">{exitDateTime.time}</Typography>
                                                </Box>
                                            </Box>
                                        ) : (
                                            <Chip
                                                label="N/A"
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            )}
        </Container>
    </ThemeProvider>);
};

export default RecognitionScreen;